<template>
  <main class="schedule">
    <div class="schedule__button-back">
      <button-back />
    </div>
    <div class="schedule-header">
      <h1 class="schedule__title">
        <span>
          График работы
        </span>
        <button
          type="button"
          v-on:click="showHintDialog = true; howToSetUpReachGoal()"
        >
          ? Как настроить
        </button>
      </h1>
      <next-step-setting-instruction
        v-bind:current-route-name="$route.name"
        v-bind:initial-steps="availableInstructionSteps"
      >
        <div class="schedule__buttons-container">
          <popover-menu
            offset-y
            top
            v-bind:close-on-content-click="true"
            v-model="isShownPrintDocumentsDialog"
            v-if="!isMobile"
          >
            <div class="schedule__popup__container">
              <label class="schedule__popup__radio">
                <input type="radio" v-on:click="printDoctorScheduleByMonth" />
                <span>Врач</span>
              </label>
              <label class="schedule__popup__radio">
                <input type="radio" v-on:click="printAssistantScheduleByMonth" />
                <span>Ассистент</span>
              </label>
              <label class="schedule__popup__radio">
                <input type="radio" v-on:click="printAdministratorScheduleByMonth" />
                <span>Администратор</span>
              </label>
            </div>
            <template v-slot:trigger="{ listenerObject }">
              <button
                v-if="!isMobile"
                type="button"
                class="schedule__button-print"
                v-on="listenerObject"
              >Печать</button>
            </template>
          </popover-menu>
          <button
            type="button"
            class="schedule__button-copy"
            v-on:click="onShowCopyWorkScheduleDialog"
          >
            Продлить график
          </button>
        </div>
      </next-step-setting-instruction>
      <div class="schedule-header__row">
        <year-month-switcher v-model="currentDate" />
      </div>
    </div>
    <div class="schedule-wrap">
      <div
        class="schedule-table scroller-container"
        ref="scheduleTable"
        v-dragscroll:nochilddrag
        v-on:dragscrollstart="isDragging = true"
        v-on:dragscrollend="isDragging = false"
      >
        <div class="schedule-table__header" ref="scheduleTableHeader">
          <div class="schedule__unit">
            <span class="schedule__unit__caption">График работы клиники</span>
            <div class="schedule__actions">
              <popover-menu>
                <template v-slot:trigger="{ listenerObject }">
                  <input
                    id="schedule-edit-mode-clinic-button-parent"
                    class="schedule__actions__button-select"
                    title="Выбор рабочих дней"
                    type="button"
                    v-on="listenerObject" />
                </template>
                <form-work-mode
                  v-bind:has-edit-schedule="hasEditSchedule"
                  v-bind:initial-mode="workMode.name"
                  v-bind:initial-weekdays="workMode.weekdays"
                  v-on:submit="onSubmitWorkMode"
                />
              </popover-menu>
              <span class="schedule__actions__text">
                {{ workStartsTime }} - {{ workEndsTime }}
              </span>
              <popover-menu v-if="hasEditSchedule" overflow-visible>
                <template v-slot:trigger="{ listenerObject }">
                  <input
                    id="schedule-edit-time-clinic-button-parent"
                    class="schedule__actions__button-edit"
                    title="Редактирование рабочего времени"
                    type="button"
                    v-on="listenerObject" />
                </template>
                <form-daily-time-range
                  v-bind:suggestion-hours="suggestedHoursForClinic"
                  v-bind:initial-from="workStartsTime"
                  v-bind:initial-to="workEndsTime"
                  v-on:submit="onSubmitWorkTime" />
              </popover-menu>
            </div>
          </div>
          <div class="schedule__days__body">
            <div class="schedule__days__row dates">
              <div
                class="schedule__days__cell data"
                v-for="(day, date) in scheduleDays"
                v-bind:class="day.tags"
                v-bind:key="date"
              >
                <div>{{ day.number }}</div>
                <div>{{ day.min_name }}</div>
              </div>
            </div>
            <div class="schedule__days__row">
              <button
                v-for="day in scheduleDays"
                v-bind:key="day.date"
                class="schedule__days__cell clinic"
                type="button"
                v-bind:class="day.tags"
                v-bind:disabled="day.tags.includes('uneditable') || !hasEditSchedule"
                v-bind:title="day.date"
                v-on:click="onClickDay(day, day.date, $event)"
              >
                <template v-if="day.work_period">
                  <time v-bind:datetime="day.work_period.starts_datetime">
                    {{ day.work_period.starts_time }}
                  </time>
                  <time v-bind:datetime="day.work_period.ends_datetime">
                    {{ day.work_period.ends_time }}
                  </time>
                </template>
              </button>
            </div>
          </div>
        </div>
        <div
          class="schedule__days__row"
          v-bind:key="employee.id"
          v-for="employee in employees"
        >
          <div class="schedule__unit employee">
            <div class="schedule__unit__card">
              <div class="schedule__unit__row">
                <img
                  v-if="employee.photo_url"
                  alt="Аватар"
                  class="schedule__employee__avatar"
                  v-bind:src="employee.photo_url"
                  v-bind:title="employee.name" />
                <div
                  v-else
                  class="schedule__employee__initials">
                  {{ getEmployeeInitials(employee) }}
                </div>
                <div class="schedule__employee__column">
                  <button
                    class="schedule__employee__name"
                    type="button"
                    v-bind:disabled="isDisabledViewEmployee"
                    v-on:click="fetchEmployeeProfile(employee.id)">
                    {{ employee.name }}
                  </button>
                  <span
                    v-if="employee.speciality_name"
                    class="schedule__employee__speciality"
                  >
                    {{ employee.speciality_name }}
                  </span>
                  <span
                    v-else
                    class="schedule__employee__speciality"
                  >
                    {{ employee.role_name }}
                  </span>
                </div>
              </div>
              <div class="schedule__actions">
                <popover-menu custom-class="form-employee-work-popover">
                  <template v-slot:trigger="{ listenerObject }">
                    <input
                      class="schedule__actions__button-select"
                      id="schedule-edit-mode-doctor-button-parent"
                      title="Выбор рабочих дней"
                      type="button"
                      v-on="listenerObject" />
                  </template>
                  <form-employee-work-mode
                    v-bind:has-edit-schedule="hasEditSchedule"
                    v-bind:initial-work-mode-options="
                      getEmployeeWorkModeOptions(employee.work_mode)
                    "
                    v-bind:initial-from="workStartsTime"
                    v-bind:initial-to="workEndsTime"
                    v-bind:initial-work-mode="getEmployeeWorkModeId(employee.work_mode)"
                    v-bind:suggested-hours="suggestedHours"
                    v-bind:assistants="assistantsList"
                    v-bind:initial-assistant-id="employee.assistant_id"
                    v-bind:menu-settings="{ maxWidth: 255 }"
                    v-on="createListenersEmployeeWorkMode(employee.id)"
                    v-on:submit-assistant-id="setEmployeeAssistant(employee.id, $event)"
                    v-on:clear-work-schedule="clearWorkSchedule(employee.id)"
                  />
                </popover-menu>
                <popover-menu overflow-visible>
                  <template v-slot:trigger="{ listenerObject }">
                    <input
                      class="schedule__actions__button-edit"
                      title="Редактирование рабочего времени"
                      type="button"
                      v-on="listenerObject" />
                    <span class="schedule__actions__text">
                      {{ employee.work_starts_time }} - {{ employee.work_ends_time }}
                    </span>
                  </template>
                  <form-daily-time-range
                    v-bind:initial-from="employee.work_starts_time"
                    v-bind:initial-to="employee.work_ends_time"
                    v-bind:suggestion-hours="suggestedHours"
                    v-on:submit="onSubmitEmployeeWorkTime(
                      employee.id,
                      ...arguments,
                    )" />
                </popover-menu>
              </div>
            </div>
          </div>
          <div
            class="schedule__days__body schedule__days__row scroller-child"
            v-bind:style="{ cursor: isDragging ? 'grabbing' : 'grab' }"
            data-dragscroll
          >
            <button
              class="schedule__days__cell"
              type="button"
              v-bind:class="scheduleDay.tags"
              v-bind:disabled="scheduleDay.tags.includes('uneditable') || !hasEditSchedule"
              v-bind:key="`employee-${employee.id}-${scheduleDay.date}`"
              v-bind:style="scheduleDay.periods[0] ? { 'background-color': scheduleDay.periods[0].color } : {}"
              v-bind:title="scheduleDay.title"
              v-for="scheduleDay in employee.schedule_days"
              v-on:click="onClickEmployeeDay(employee, $event, scheduleDay)"
            >
              <template v-if="scheduleDay.periods[0]">
                <time v-bind:datetime="scheduleDay.periods[0].starts_datetime">
                  {{ scheduleDay.periods[0].starts_time }}
                </time>
                <time v-bind:datetime="scheduleDay.periods[0].ends_datetime">
                  {{ scheduleDay.periods[0].ends_time }}
                </time>
              </template>
            </button>
          </div>
        </div>
        <template v-if="hasAdministrators">
          <h3
            data-dragscroll
            v-bind:style="{ cursor: isDragging ? 'grabbing' : 'grab' }"
            class="schedule__subtitle">Другой персонал</h3>
          <div
            class="schedule__days__row"
            v-bind:key="administrator.id"
            v-for="administrator in administrators"
          >
            <div class="schedule__unit employee">
              <div class="schedule__unit__card">
                <div class="schedule__unit__row">
                  <div
                    class="schedule__employee__initials">
                    {{ getEmployeeInitials(administrator) }}
                  </div>
                  <div class="schedule__employee__column">
                    <button
                      class="schedule__employee__name"
                      type="button"
                      v-bind:disabled="isDisabledViewEmployee"
                      v-on:click="fetchEmployeeProfile(administrator.id)">
                      {{ administrator.name }}
                    </button>
                    <span class="schedule__employee__speciality">
                      {{ administrator.role_name }}
                    </span>
                  </div>
                </div>
                <div class="schedule__actions">
                  <popover-menu custom-class="form-employee-work-popover">
                    <template v-slot:trigger="{ listenerObject }">
                      <input
                        class="schedule__actions__button-select"
                        id="schedule-edit-mode-doctor-button-parent"
                        title="Выбор рабочих дней"
                        type="button"
                        v-on="listenerObject" />
                    </template>
                    <form-employee-work-mode
                      hide-assistants
                      v-bind:has-edit-schedule="hasEditSchedule"
                      v-bind:initial-work-mode-options="
                        getEmployeeWorkModeOptions(administrator.work_mode)
                      "
                      v-bind:initial-from="workStartsTime"
                      v-bind:initial-to="workEndsTime"
                      v-bind:initial-work-mode="getEmployeeWorkModeId(administrator.work_mode)"
                      v-bind:suggested-hours="suggestedHours"
                      v-bind:assistants="assistantsList"
                      v-bind:menu-settings="{ maxWidth: 255 }"
                      v-on="createListenersAdministratorWorkMode(administrator.id)"
                      v-on:clear-work-schedule="onClearAdministratorWorkSchedule(administrator.id)"
                    />
                  </popover-menu>
                  <popover-menu overflow-visible>
                    <template v-slot:trigger="{ listenerObject }">
                      <input
                        class="schedule__actions__button-edit"
                        title="Редактирование рабочего времени"
                        type="button"
                        v-on="listenerObject" />
                      <span class="schedule__actions__text">
                        {{ administrator.work_starts_time }} - {{ administrator.work_ends_time }}
                      </span>
                    </template>
                    <form-daily-time-range
                      v-bind:initial-from="administrator.work_starts_time"
                      v-bind:initial-to="administrator.work_ends_time"
                      v-bind:suggestion-hours="suggestedHours"
                      v-on:submit="onSubmitAdministratorWorkTime(
                        administrator.id,
                        ...arguments,
                      )" />
                  </popover-menu>
                </div>
              </div>
            </div>
            <div
              class="schedule__days__body schedule__days__row scroller-child"
              v-bind:style="{ cursor: isDragging ? 'grabbing' : 'grab' }"
              data-dragscroll
            >
              <button
                class="schedule__days__cell"
                v-for="scheduleDay in administrator.schedule_days"
                type="button"
                v-bind:key="`employee-${administrator.id}-${scheduleDay.date}`"
                v-bind:class="scheduleDay.tags"
                v-bind:disabled="scheduleDay.tags.includes('uneditable') || !hasEditSchedule"
                v-bind:style="scheduleDay.periods[0] ? { 'background-color': scheduleDay.periods[0].color } : {}"
                v-bind:title="scheduleDay.title"
                v-on:click="onClickAdministratorDay($event, administrator, scheduleDay)"
              >
                <template v-if="scheduleDay.periods[0]">
                  <time v-bind:datetime="scheduleDay.periods[0].starts_datetime">
                    {{ scheduleDay.periods[0].starts_time }}
                  </time>
                  <time v-bind:datetime="scheduleDay.periods[0].ends_datetime">
                    {{ scheduleDay.periods[0].ends_time }}
                  </time>
                </template>
              </button>
            </div>
          </div>
        </template>
      </div>
      <iframe
        style="display: none"
        ref="printView"
        title="printView"
      />
      <div class="schedule__info" v-if="isHasEmployees">
        Запись пациентов к врачу осуществляется в разделе
        <router-link :to="{ name: 'ClinicPanelAppointmentScheduleEmployees' }">Расписание</router-link>
      </div>
      <div class="schedule__info" v-else>
        Добавьте новых <router-link
          :to="{ name: 'ClinicPanelEmployeeList' }"
        >сотрудников</router-link>, чтобы построить график работы
      </div>
    </div>
    <popover-menu
      absolute
      v-bind:key="selectedEmployeeDayProps.key"
      v-bind:position-x="selectedEmployeeDayProps.x"
      v-bind:position-y="selectedEmployeeDayProps.y"
      v-bind:initial-shown="isShowFormEmployeeWorkDay"
      v-on:input="onCancelFormEmployeeWorkDay"
    >
      <form-employee-work-day
        v-bind:available-work-cabinet-ids="selectedEmployeeDayProps.availableWorkCabinetIds"
        v-bind:cabinet-list="cabinetList"
        v-bind:date-title="selectedEmployeeDayProps.dateTitle"
        v-bind:datetime="selectedEmployeeDayProps.datetime"
        v-bind:has-edit-schedule="hasEditSchedule"
        v-bind:initial-periods="selectedEmployeeDayProps.initialPeriods"
        v-bind:name="selectedEmployeeDayProps.employeeName"
        v-bind:suggested-hours="selectedEmployeeDayProps.suggestedHours"
        v-bind:assistants="assistantsList"
        v-on:cancel="onCancelFormEmployeeWorkDay"
        v-on:submit="onSubmitEmployeeWorkDay(
          selectedEmployeeDayProps.employeeId,
          $event,
        )"
      />
    </popover-menu>
    <popover-menu
      v-if="selectedAdministratorDay"
      absolute
      v-bind:key="selectedAdministratorDay.key"
      v-bind:position-x="selectedAdministratorDay.x"
      v-bind:position-y="selectedAdministratorDay.y"
      v-bind:initial-shown="selectedAdministratorDay !== null"
      v-on:input="onCancelFormAdministratorWorkDay"
    >
      <form-administrator-work-day
        v-bind:date-title="selectedAdministratorDay.dateTitle"
        v-bind:datetime="selectedAdministratorDay.datetime"
        v-bind:has-edit-schedule="hasEditSchedule"
        v-bind:initial-periods="selectedAdministratorDay.initialPeriods"
        v-bind:name="selectedAdministratorDay.employeeName"
        v-bind:suggested-hours="selectedAdministratorDay.suggestedHours"
        v-on:cancel="onCancelFormAdministratorWorkDay"
        v-on:submit="onSubmitAdministratorWorkDay(
          selectedAdministratorDay.employeeId,
          $event,
        )"
      />
    </popover-menu>
    <v-menu
      v-if="isShowFormClinicWorkDay"
      content-class="popover-menu-vuetify menu-visible"
      offset-overflow
      offset-y
      top
      v-bind:close-on-content-click="false"
      v-bind:position-x="selectedClinicDay.x"
      v-bind:position-y="selectedClinicDay.y"
      v-bind:value="true">
      <form-work-day
        v-bind:date-title="selectedClinicDay.dateTitle"
        v-bind:datetime="selectedClinicDay.datetime"
        v-bind:has-edit-schedule="hasEditSchedule"
        v-bind:initial-from="selectedClinicDay.from"
        v-bind:initial-to="selectedClinicDay.to"
        v-on:cancel="onCancelFormWorkDay"
        v-on:submit="onSubmitWorkDay"
      />
    </v-menu>
    <side-slide-transition>
      <confirm-side-dialog
        v-if="hasEmployeeProfile"
        label="Редактирование сотрудника"
        v-on:cancel="resetEmployeeProfile"
        v-bind:need-confirm="needConfirmEmployeeForm"
      >
        <form-employee-creation
          method="dialog"
          v-bind:is-open-login-settings-section="isOpenLoginSettingsSection"
          v-bind:cabinet-option-list="employeeProfileOptions.cabinetList"
          v-bind:confidentiality-level-list="employeeProfileOptions.confidentialityLevels"
          v-bind:employee-position-option-list="employeeProfileOptions.employeePositionList"
          v-bind:employee-role-list="employeeProfileOptions.employeeRoleList"
          v-bind:employee-specialty-option-list="employeeProfileOptions.employeeSpecialtyList"
          v-bind:has-egisz="employeeProfileOptions.hasEgisz"
          v-bind:initial-permissions="selectedEmployeePermission"
          v-bind:input-errors="employeeProfileInputErrors"
          v-bind:is-open-optional-section="isOpenOptionalSection"
          v-bind:is-open-egis-section="isOpenEgisSection"
          v-bind:nsi-position-list="employeeProfileOptions.nsiPositionList"
          v-bind:time-step-option-list="employeeProfileOptions.timeStepList"
          v-bind="employeeProfileProps"
          v-on:change-values="onChangeFieldValues"
          v-on:collapse-optional-section="onCollapseOptionalSection"
          v-on:collapse-egis-section="onCollapseEgisSection"
          v-on:change-password="onChangePassword"
          v-on:submit-editing="onSubmitEmployeeEdit"
        >
          <template slot="footer">
            <menu class="schedule__employee-form__buttons">
              <button
                class="schedule__employee-form__button-cancel"
                type="button"
                v-on:click="resetEmployeeProfile">Отмена</button>
              <button
                class="schedule__employee-form__button-save"
                type="submit">Сохранить</button>
            </menu>
          </template>
        </form-employee-creation>
      </confirm-side-dialog>
    </side-slide-transition>
    <confirm-side-dialog
      label="Как заполнить график работы клиники"
      v-if="showHintDialog"
      v-on:cancel="showHintDialog = false"
      class="schedule__modal"
    >
      <br>
      <p>
        После создания кресел, ролей и сотрудников
        можно заполнить график работы клиники.</p>
      <p>
        Сначала укажите график работы клиники
        и выберите период, в течение которого
        этот график будет действовать.
        Подтвердите изменения кнопкой «ОК».
      </p>
      <zoom-image
        v-bind:src="require('@/assets/img/help/schedule-1.png')"
        alt="график работы клиники"
        class="schedule__modal__img"
      />
      <p>Затем укажите время работы клиники. Подтвердите изменения кнопкой «ОК».</p>
      <zoom-image
        v-bind:src="require('@/assets/img/help/schedule-2.png')"
        alt="время работы клиники"
        class="schedule__modal__img"
      />
      <p>
        Теперь вы можете назначить график работы для каждого сотрудника.
        Нажмите на стрелочку возле фамилии сотрудника и
        задайте нужные параметры. Укажите, в течение
        какого периода действует такой график.
      </p>
      <zoom-image
        v-bind:src="require('@/assets/img/help/schedule-3.png')"
        alt="назначить график работы для каждого сотрудника"
        class="schedule__modal__img"
      />
      <ul>
        <li>
          Если время работы сотрудника совпадает
          с общим временем работы клиники, выберите пункт
          «Работает как организация».
        </li>
        <li>
          Если сотрудник работает по графику
          «Дни через дни», выберите одноименный параметр.
        </li>
        <li>Если сотрудник работает по определенным
          дням недели, выберите соответствующий пункт.
        </li>
        <li>
          Если у сотрудника свободный график - выбирайте последний пункт.
        </li>
      </ul>
      <p>Для того, чтобы назначить сотруднику рабочий день
        (при свободном графике), нажмите мышкой на этот день:
      </p>
      <zoom-image
        v-bind:src="require('@/assets/img/help/schedule-4.png')"
        alt="назначить сотруднику рабочий день"
        class="schedule__modal__img"
      />
      <p>
        День окрасится в цвет кресла сотрудника
        и станет рабочим. Чтобы указать рабочее время
        на данный день, нажмите мышкой на этот день еще раз.
        В открывшемся окне назначьте нужные параметры:
        кресло и время работы. Если ошиблись с датой,
        то снова верните выходной день: проставьте
        галочку рядом в графе «Выходной». Подтвердите изменения кнопкой «ОК».
      </p>
      <zoom-image
        v-bind:src="require('@/assets/img/help/schedule-5.png')"
        alt="назначить нужные параметры"
        class="schedule__modal__img"
      />
      <p>
        Цифры на рабочем дне обозначают время
        работы сотрудника (пример: 10:40-11:40).
        Цвет дня - это выбранное кресло
        (пример: в понедельник сотрудник принимает
        в одном кресле, а в среду уже в другом):
      </p>
      <zoom-image
        v-bind:src="require('@/assets/img/help/schedule-6.png')"
        alt="Пример рабочего дня"
        class="schedule__modal__img"
      />
    </confirm-side-dialog>
    <on-boarding-dialog
      :is-page-ready="isPageReady"
      :is-show-screen="isShowOnBoardingDialog"
      :is-show-button-edit-mode-clinic-schedule="isShowButtonEditModeClinicSchedule"
      :is-show-button-edit-time-clinic-schedule="isShowButtonEditTimeClinicSchedule"
      :is-show-button-edit-mode-doctor-schedule="isShowButtonEditModeDoctorSchedule"
      @showed="showedOnboarding"
    >
      <input
        v-if="isShowButtonEditModeClinicSchedule"
        id="schedule-edit-mode-clinic-button-child"
        class="schedule__actions__button-select-show"
        type="button"
      />
      <input
        v-if="isShowButtonEditTimeClinicSchedule"
        id="schedule-edit-time-clinic-button-child"
        class="schedule__actions__button-edit-show"
        type="button"
      />
      <input
        v-if="isShowButtonEditModeDoctorSchedule"
        id="schedule-edit-mode-doctor-button-child"
        class="schedule__actions__button-select-show"
        type="button"
      />
    </on-boarding-dialog>
    <modal-dialog
      content-class="schedule-copy-dialog"
      title=""
      v-model="shownCopyWorkScheduleDialog"
      title-center
      max-width="555px"
      with-cross
    >
      <template v-slot:before-title>
        <div class="schedule-copy-dialog__header">
          <span class="schedule-copy-dialog__text">Копировать график с</span>
          <input-select
            need-use-custom-item
            option-title-key='title'
            option-value-key='value'
            title=''
            v-bind:menu-props="{
              offsetY: true,
              maxWidth: '110px',
              minWidth: '100px !important',
            }"
            v-bind:options="[currentMonth, prevMonth]"
            v-bind:value="currentMonth.value"
            v-on:input="onChangeSelectedDateToCopy"
          />
        </div>
      </template>
      <p class="schedule-copy-dialog__note">
        <span class="schedule-copy-dialog__note__sign">!</span>
        Для свободного графика копирование не доступно
      </p>
      <div class="schedule-copy-dialog__employee-rows">
        <div class="schedule-copy-dialog__employee-row">
          <input-checkbox
            class="schedule-copy-dialog__field"
            v-model="selectedAllEmployeesToCopy"
            v-on:input="onSelectAllEmployeesToCopy"
          />
          <span class="schedule-copy-dialog__field__text">Выбрать всех</span>
        </div>
        <div
          class="schedule-copy-dialog__employee-row"
          v-for="employee in employeesToCopyWorkSchedule"
          v-bind:key="`selected-employee-${employee.id}`"
        >
          <input-checkbox
            class="schedule-copy-dialog__field"
            v-bind:value="selectedEmployeesToCopySchedule[employee.id]"
            v-on:input="onCopyEmployeeScheduleInput(employee.id, $event)"
          />
          <div class="schedule-copy-dialog____employee-row__wrapper">
            <span class="schedule-copy-dialog__employee-row__name">
              {{ employee.name }}
            </span>
            <span class="schedule-copy-dialog__employee-row__speciality">
              {{ employee.speciality_name }}
            </span>
          </div>
        </div>
      </div>
      <template v-slot:actions>
        <button
          class="schedule__button-submit"
          v-bind:disabled="isAwaitRequest"
          type="button"
          v-on:click="onSubmitCopyEmployeesWorkSchedule"
        >
          Перенести график на {{ nextMonth.title }}
        </button>
      </template>
    </modal-dialog>
  </main>
</template>
<script>
import FormAdministratorWorkDay from '@/components/FormAdministratorWorkDay.vue';
import FormDailyTimeRange from '@/components/FormDailyTimeRange.vue';
import FormEmployeeWorkDay from '@/components/FormEmployeeWorkDay.vue';
import FormEmployeeWorkMode from '@/components/FormEmployeeWorkMode.vue';
import FormWorkDay from '@/components/FormWorkDay.vue';
import FormWorkMode from '@/components/FormWorkMode.vue';
import PopoverMenu from '@/components/PopoverMenu.vue';
import YearMonthSwitcher from '@/components/YearMonthSwitcher.vue';
import notifierMixin from '@/mixins/notifierMixin';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import FormEmployeeCreation from '@/components/FormEmployeeCreation.vue';
import employeeProfileMixin from '@/mixins/employeeProfileMixin';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import settingInstructionMixin from '@/mixins/settingInstructionMixin';
import { Maybe } from 'monet';
import NextStepSettingInstruction from '@/components/NextStepSettingInstruction.vue';
import ZoomImage from '@/components/ZoomImage.vue';
import ButtonBack from '@/components/ButtonBack.vue';
import mobileMixin from '@/mixins/mobileMixin';
import { builtInDialogMixin, DialogResult } from '@/components/ClinicPanelDialog.vue';
import timeMixin from '@/mixins/timeMixin';
import onBoardingMixin from '@/mixins/onBoardingMixin';
import OnBoardingDialog from '@/components/OnBoardingDialog.vue';
import onBoardingShowMixin from '@/mixins/onBoardingShowMixin';
import { GOAL_CLICK_HOW_TO_SET_UP_BUTTON, GOAL_COPY_EMPLOYEES_WORK_SCHEDULE } from '@/plugins/metric';
import { dragscroll } from 'vue-dragscroll';
import ModalDialog from '@/components/ModalDialog.vue';
import InputSelect from '@/components/InputSelect.vue';
import InputCheckbox from '@/components/InputCheckbox.vue';

/**
 * @description Перевести первый символ строки в верхний регистр.
 * @param {string} word Строка.
 * @returns {string}
 */
const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1);

/**
 * @description Действие изменение базового время работы сотрудника.
 * @type {string}
 */
const ACTION_CHANGE_EMPLOYEE_WORK_TIME = 'change-employee-work-time';

/**
 * @description Примесь администраторов.
 */
const administratorMixin = {
  data() {
    return {
      selectedAdministratorDay: null,
    };
  },
  methods: {
    /**
     * @description Создать объект слушателей событий отправки рабочего режима администратора.
     * @param {number} employeeId Идентификатор сотрудника.
     * @returns {any}
     */
    createListenersAdministratorWorkMode(employeeId) {
      const byClinic = (closePopover) => {
        this.closePopover = closePopover;
        this
          .$workScheduleGateway
          .setAdministratorWorkMonthByClinic(employeeId, this.yearAndMonth)
          .then(this.onGatewayResponse);
      };
      const byWeekdays = (weekdays, startsDate, closePopover) => {
        this.closePopover = closePopover;
        this
          .$workScheduleGateway
          .setAdministratorWorkMonthByWeekdays(
            employeeId,
            startsDate,
            weekdays,
          )
          .then(this.onGatewayResponse);
      };
      const throughDays = (
        numberOffDays,
        startsDate,
        workDays,
        closePopover,
      ) => {
        this.closePopover = closePopover;
        this
          .$workScheduleGateway
          .setAdministratorWorkMonthThroughDays(
            employeeId,
            numberOffDays,
            startsDate,
            workDays,
          )
          .then(this.onGatewayResponse);
      };
      const evenOddDays = (
        evenDaysStartsDate,
        evenDaysWorkTimes,
        oddDaysStartsDate,
        oddDaysWorkTimes,
        closePopover,
      ) => {
        this.closePopover = closePopover;
        this
          .$workScheduleGateway
          .setAdministratorWorkMonthEvenOddDays(
            employeeId,
            evenDaysStartsDate,
            evenDaysWorkTimes,
            oddDaysStartsDate,
            oddDaysWorkTimes,
          )
          .then(this.onGatewayResponse);
      };
      const free = (closePopover) => {
        this.closePopover = closePopover;
        this
          .$workScheduleGateway
          .setAdministratorWorkModeByFree(employeeId, this.yearAndMonth)
          .then(this.onGatewayResponse);
      };
      return {
        'submit-by-clinic': byClinic,
        'submit-by-weekdays': byWeekdays,
        'submit-through-days': throughDays,
        'submit-even-odd-days': evenOddDays,
        'submit-free': free,
      };
    },
    onClickAdministratorDay(event, administrator, scheduleDay) {
      if (this.isClinicDayOff(scheduleDay.date)) {
        this.notifyImportanceFail(
          'Этот день в графике клиники установлен как выходной.'
            + ' Сначала измените график работы клиники',
        );
        return;
      }
      const {
        date: datetime,
        periods,
        suggested_hours: suggestedHours,
        title: dateTitle,
      } = scheduleDay;
      const { clientX: x, clientY: y } = event;
      const selectDay = (initialPeriods) => {
        this.selectedAdministratorDay = {
          dateTitle,
          datetime,
          employeeId: administrator.id,
          employeeName: administrator.name,
          initialPeriods,
          key: `${administrator.id}-${datetime}`,
          suggestedHours,
          x,
          y,
        };
      };
      if (periods.length > 0) {
        selectDay(periods.map((period) => ({ from: period.starts_time, to: period.ends_time })));
      } else {
        const from = administrator.work_starts_time || '00:00';
        const to = administrator.work_ends_time || '00:00';
        selectDay([{ from, to }]);
      }
    },
    onCancelFormAdministratorWorkDay() {
      this.selectedAdministratorDay = null;
    },
    onSubmitAdministratorWorkDay(administratorId, day) {
      this.closePopover = null;
      if (day.isOff) {
        this
          .$workScheduleGateway
          .setAdministratorDayOff(administratorId, day.date)
          .then(this.onGatewayResponse);
      } else {
        this
          .$workScheduleGateway
          .setAdministratorWorkDay(
            administratorId,
            day.date,
            day.workTimes,
          )
          .then(this.onGatewayResponse);
      }
    },
    /**
     * @description Обработать отправку базового времени работы администратора.
     * @param {number} employeeId Идентификатор сотрудника.
     * @param {string} startsTime Время начала.
     * @param {string} endsTime   Время окончания.
     * @returns {void}
     */
    onSubmitAdministratorWorkTime(employeeId, startsTime, endsTime, closePopover) {
      this.closePopover = closePopover;
      this
        .$workScheduleGateway
        .setAdministratorBasicWorkTime(employeeId, startsTime, endsTime)
        .then(this.onGatewayResponse);
    },
    /**
     * @description Очистить график работы администратора.
     * @param {number} employeeId
     * @returns {void}
     */
    async onClearAdministratorWorkSchedule(employeeId) {
      const monthTitle = capitalize(this.currentDate.toLocaleString('ru', { month: 'long' }));
      const yearTitle = this.currentDate.getFullYear();
      const dialog = await this.showAcceptDialog(`Рабочие часы за ${monthTitle} ${yearTitle} будут удалены. Очистить график?`);
      if (dialog.result !== DialogResult.YES) {
        return;
      }
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.fetchScheduleByMonth(this.yearAndMonth);
      };
      const onFail = (error) => {
        if (error.title) {
          this.notifyFail(error.title);
        }
      };
      this
        .$workScheduleGateway
        .clearAdministratorWorkSchedule(employeeId, this.yearAndMonth)
        .then((result) => { result.fold(onFail, onSuccess); });
    },
  },
};

/**
 * @description Примесь копирования графика работы сотрудников.
 * @type {Vue}
 */
const copyEmployeeWorkScheduleMixin = {
  computed: {
    /**
     * @description Базовая дата для расчетов (текущий отображаемый месяц).
     * @returns {Date}
     */
    baseDateForCopy() {
      return new Date(this.yearAndMonth ?? '');
    },
    /**
     * @description Текущий месяц.
     * @returns {{title: string, value: Date}}
     */
    currentMonth() {
      return {
        title: this.getMonthTitle(this.baseDateForCopy),
        value: this.baseDateForCopy,
      };
    },
    /**
     * @description Предыдущий месяц.
     * @returns {{title: string, value: Date}}
     */
    prevMonth() {
      const prevDate = new Date(this.baseDateForCopy);
      prevDate.setMonth(prevDate.getMonth() - 1);
      return {
        title: this.getMonthTitle(prevDate),
        value: prevDate,
      };
    },
    /**
     * @description Следующий месяц.
     * @returns {{title: string, value: Date}}
     */
    nextMonth() {
      const nextDate = new Date(this.baseDateForCopy);
      nextDate.setMonth(nextDate.getMonth() + 1);
      return {
        title: this.getMonthTitle(nextDate),
        value: nextDate,
      };
    },
    /**
     * @description Объект с состоянием выбора сотрудников для копирования.
     * @returns {Object<string, boolean>}
     */
    selectedEmployeesToCopySchedule() {
      return Object.fromEntries(
        this.employees.map(({ id }) => [
          id,
          this.selectedEmployees.includes(id),
        ]),
      );
    },
    /**
     * @description Сотрудники на копирование графика работы.
     * @return {Array<Object}
     */
    employeesToCopyWorkSchedule() {
      return this
        .employees
        .filter((employee) => ['free', 'empty'].includes(this.getEmployeeWorkModeId(employee.work_mode)) === false);
    },
  },
  data() {
    return {
      shownCopyWorkScheduleDialog: false,
      selectedEmployees: [],
      selectedDateToCopy: null,
      selectedAllEmployeesToCopy: false,
      isAwaitRequest: false,
    };
  },
  methods: {
    /**
     * @description Получить наименование месяца.
     * @param {Date} date
     * @returns {string}
     */
    getMonthTitle(date) {
      return date.toLocaleString('ru', { month: 'long' });
    },
    /**
     * @description Обработать изменение выбранной даты для копирования.
     * @param {Date} value
     */
    onChangeSelectedDateToCopy(value) {
      this.selectedDateToCopy = value;
    },
    /**
     * @description Обработать показ диалогового окна копирования графика работы.
     * @returns {void}
     */
    onShowCopyWorkScheduleDialog() {
      this.selectedDateToCopy = this.currentMonth.value;
      this.shownCopyWorkScheduleDialog = true;
    },
    /**
     * @description Обработать выбор сотрудника для копирования графика.
     * @param {string}  employeeId Идентификатор сотрудника.
     * @param {boolean} isChecked  Выбран ли сотрудник.
     */
    onCopyEmployeeScheduleInput(employeeId, isChecked) {
      const selected = new Set(this.selectedEmployees);
      if (isChecked) {
        selected.add(employeeId);
      } else {
        selected.delete(employeeId);
      }
      this.selectedEmployees = Array.from(selected);
      const allCopiableEmployees = this.employees.filter(
        (item) => this.getEmployeeWorkModeId(item.work_mode) !== 'free',
      );
      this.selectedAllEmployeesToCopy = this.selectedEmployees.length > 0
        && this.selectedEmployees.length === allCopiableEmployees.length;
    },
    /**
     * @description Обработать выбор всех сотрудников для копирования.
     * @param {boolean} value
     */
    onSelectAllEmployeesToCopy(value) {
      if (value) {
        this.selectedEmployees = this.employees
          .filter((item) => this.getEmployeeWorkModeId(item.work_mode) !== 'free')
          .map((item) => item.id);
      } else {
        this.selectedEmployees = [];
      }
      this.selectedAllEmployeesToCopy = value;
    },
    /**
     * @description Обработать отправку копирования графиков работы сотрудников.
     */
    onSubmitCopyEmployeesWorkSchedule() {
      if (this.selectedEmployees.length === 0) {
        this.notifyFail('Выберите хотя бы одного сотрудника');
        return;
      }
      this.isAwaitRequest = true;
      const formatDateToYearMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        return `${year}-${String(month).padStart(2, '0')}`;
      };
      const fromYearMonth = formatDateToYearMonth(this.selectedDateToCopy);
      const toYearMonth = formatDateToYearMonth(this.nextMonth.value);
      const onSuccess = (response) => {
        this.notifySuccess(response.title || 'График успешно скопирован');
        this.shownCopyWorkScheduleDialog = false;
        this.selectedEmployees = [];
        this.selectedAllEmployeesToCopy = false;
        this.fetchScheduleByMonth(this.yearAndMonth);
        this.copyEmployeeWorkScheduleReachGoal();
      };
      const onFail = (error) => {
        if (error.title) {
          this.notifyFail(error.title);
        } else {
          this.notifyFail('Произошла ошибка при копировании графика');
        }
      };
      this
        .$workScheduleGateway
        .copyEmployeesWorkSchedule(fromYearMonth, toYearMonth, this.selectedEmployees)
        .then((result) => {
          result.fold(onFail, onSuccess);
        })
        .finally(() => {
          this.isAwaitRequest = false;
        });
    },
  },
};

/**
 * @description Компонент рабочего распорядка.
 * @type {Vue}
 */
export default {
  directives: {
    dragscroll,
  },
  components: {
    InputSelect,
    InputCheckbox,
    ModalDialog,
    OnBoardingDialog,
    ButtonBack,
    ZoomImage,
    NextStepSettingInstruction,
    FormAdministratorWorkDay,
    FormDailyTimeRange,
    FormEmployeeWorkDay,
    FormEmployeeWorkMode,
    FormWorkDay,
    FormWorkMode,
    PopoverMenu,
    YearMonthSwitcher,
    SideSlideTransition,
    FormEmployeeCreation,
    ConfirmSideDialog,
  },
  computed: {
    /**
     * Доступ как показу "Разрешить доступ к системе"
     *
     * @return {boolean}
     */
    isOpenLoginSettingsSection() {
      return false;
    },
    /**
     * Имеются ли работники в клинике.
     *
     * @return {boolean}
     */
    isHasEmployees() {
      return this.employees.length > 0;
    },
    /**
     * Показываем представление "Изменить режим работы клиники"
     *
     * @return {boolean}
     */
    isShowButtonEditModeClinicSchedule() {
      return this.step?.notShowedView === 'schedule-button-clinic-mode-edit';
    },
    /**
     * Показываем представление "Изменить время работы клиники"
     *
     * @return {boolean}
     */
    isShowButtonEditTimeClinicSchedule() {
      return this.step?.notShowedView === 'schedule-button-clinic-time-edit';
    },
    /**
     * Показываем представление "Изменить время работы врача"
     *
     * @return {boolean}
     */
    isShowButtonEditModeDoctorSchedule() {
      return this.step?.notShowedView === 'schedule-button-doctor-mode-edit'
        && this.employees.length > 0;
    },
    /**
     * Показываем ОнБординг Окно
     *
     * @return {boolean}
     */
    isShowOnBoardingDialog() {
      return this.isShowButtonEditModeClinicSchedule
        || this.isShowButtonEditModeDoctorSchedule
        || this.isShowButtonEditTimeClinicSchedule;
    },
    /**
     * Получить часы работы доступные для выбора клинике
     *
     * @return {[string]}
     */
    suggestedHoursForClinic() {
      return timeMixin.methods.getForClinicHours();
    },
    cabinetList: (vm) => vm
      .cabinets
      .map((item) => ({ title: item.name, value: item.id })),
    hasEditSchedule() {
      return this.actions.includes('edit-work-schedule');
    },
    isDisabledViewEmployee() {
      return !this.actions.includes('view-employee-profile');
    },
    yearAndMonth: (vm) => {
      const year = vm.currentDate.getFullYear();
      const month = vm.currentDate.getMonth() + 1; // non zero-based
      return String(year).concat('-', String(month).padStart(2, '0'));
    },
    /**
     * @description Показывать форму изменения рабочего дня клиники.
     * @type {boolean}
     */
    isShowFormClinicWorkDay: ({ selectedClinicDay }) => !!selectedClinicDay,
    /**
     * @description Показывать форму изменения рабочего дня сотрудника.
     * @type {boolean}
     */
    isShowFormEmployeeWorkDay() {
      return this.selectedEmployeeDay.isSome();
    },
    /**
     * @description Данные выбранного рабочего дня сотрудника.
     * @returns {any}
     */
    selectedEmployeeDayProps() {
      return this.selectedEmployeeDay.fold({})((day) => day);
    },
    /**
     * @description Проверка наличия другого персонала.
     * @returns {boolean}
     */
    hasAdministrators() {
      return this.administrators.length > 0;
    },
  },
  updated() {
    this.definePositionAndSetPositionsToChildComponent(
      this.isShowButtonEditModeClinicSchedule,
      '#schedule-edit-mode-clinic-button-parent',
      '#schedule-edit-mode-clinic-button-child',
    );
    this.definePositionAndSetPositionsToChildComponent(
      this.isShowButtonEditTimeClinicSchedule,
      '#schedule-edit-time-clinic-button-parent',
      '#schedule-edit-time-clinic-button-child',
    );
    this.definePositionAndSetPositionsToChildComponent(
      this.isShowButtonEditModeDoctorSchedule,
      '#schedule-edit-mode-doctor-button-parent',
      '#schedule-edit-mode-doctor-button-child',
    );
  },
  created() {
    this.fetchAssistantList();
    this.fetchScheduleByMonth(this.yearAndMonth);
    this.fetchEmployeeProfileOptions();
  },
  data() {
    return {
      /**
       * @description Допустимые действия.
       * @type {string[]}
       */
      actions: [],
      administrators: [],
      cabinets: [],
      currentDate: new Date(),
      employees: [],
      needConfirmEmployeeForm: false,
      scheduleDays: [],
      suggestedHours: [],
      workEndsTime: '',
      workStartsTime: '',
      closePopover: () => {},
      /**
       * @description Выбранный рабочий день клиники.
       * @type {any}
       */
      selectedClinicDay: null,
      /**
       * @description Выбранный рабочий день сотрудника.
       * @type {Maybe}
       */
      selectedEmployeeDay: Maybe.none(),
      workMode: 'five-days',
      /**
       * @description Показывать диалоговое окно подсказки.
       * @type {boolean}
       */
      showHintDialog: false,
      /**
       * @description Список ассистентов.
       * @type {any[]}
       */
      assistantsList: [],
      /**
       * @description Показывать диалоговое окно печати.
       * @type {boolean}
       */
      isShownPrintDocumentsDialog: false,
      /**
       * Слаг шага ОнБординга
       * @type {string}
       */
      stepSlug: 'insert-schedule-work',
      /**
       * Загружена ли информация о графике работы
       * @type {boolean}
       */
      isPageReady: false,
      isDragging: false,
    };
  },
  methods: {
    /**
     * @description Обновить ассистента сотрудника.
     * @param {number} employeeId  Идентификатор сотрудника.
     * @param {number} assistantId Идентификатор ассистента.
     * @returns {any}
     */
    setEmployeeAssistant(employeeId, assistantId) {
      this
        .$workScheduleGateway
        .setEmployeeAssistant(employeeId, assistantId)
        .then(this.onGatewayResponse);
    },
    /**
     * @description Очистить график работы сотрудника.
     * @param {number} employeeId
     * @returns {void}
     */
    async clearWorkSchedule(employeeId) {
      const monthTitle = capitalize(this.currentDate.toLocaleString('ru', { month: 'long' }));
      const yearTitle = this.currentDate.getFullYear();
      const dialog = await this.showAcceptDialog(`Рабочие часы за ${monthTitle} ${yearTitle} будут удалены. Очистить график?`);
      if (dialog.result !== DialogResult.YES) {
        return;
      }
      const onSuccess = (response) => {
        this.notifySuccess(response.title);
        this.fetchScheduleByMonth(this.yearAndMonth);
      };
      const onFail = (error) => {
        if (error.title) {
          this.notifyFail(error.title);
        }
      };
      this
        .$workScheduleGateway
        .clearEmployeeWorkSchedule(employeeId, this.yearAndMonth)
        .then((result) => { result.fold(onFail, onSuccess); });
    },
    /**
     * @description Создать объект слушателей событий отправки рабочего режима
     *              сотрудника.
     * @param {number} employeeId Идентификатор сотрудника.
     * @returns {any}
     */
    createListenersEmployeeWorkMode(employeeId) {
      const byClinic = (closePopover) => {
        this.closePopover = closePopover;
        this
          .$workScheduleGateway
          .setEmployeeWorkMonthByClinic(employeeId, this.yearAndMonth)
          .then(this.onGatewayResponse);
      };
      const byWeekdays = (weekdays, startsDate, closePopover) => {
        this.closePopover = closePopover;
        this
          .$workScheduleGateway
          .setEmployeeWorkMonthByWeekdays(
            employeeId,
            startsDate,
            weekdays,
          )
          .then(this.onGatewayResponse);
      };
      const throughDays = (
        numberOffDays,
        startsDate,
        workDays,
        closePopover,
      ) => {
        this.closePopover = closePopover;
        this
          .$workScheduleGateway
          .setEmployeeWorkMonthThroughDays(
            employeeId,
            numberOffDays,
            startsDate,
            workDays,
          )
          .then(this.onGatewayResponse);
      };
      const evenOddDays = (
        evenDaysStartsDate,
        evenDaysWorkTimes,
        oddDaysStartsDate,
        oddDaysWorkTimes,
        closePopover,
      ) => {
        this.closePopover = closePopover;
        this
          .$workScheduleGateway
          .setEmployeeWorkMonthEvenOddDays(
            employeeId,
            evenDaysStartsDate,
            evenDaysWorkTimes,
            oddDaysStartsDate,
            oddDaysWorkTimes,
          )
          .then(this.onGatewayResponse);
      };
      const free = (closePopover) => {
        this.closePopover = closePopover;
        this
          .$workScheduleGateway
          .setEmployeeWorkModeByFree(employeeId, this.yearAndMonth)
          .then(this.onGatewayResponse);
      };
      return {
        'submit-by-clinic': byClinic,
        'submit-by-weekdays': byWeekdays,
        'submit-through-days': throughDays,
        'submit-even-odd-days': evenOddDays,
        'submit-free': free,
      };
    },
    /**
     * @description Получить и установить данные расписания.
     * @param {string} yearAndMonth Год и месяц.
     * @returns {void}
     */
    fetchScheduleByMonth(yearAndMonth) {
      const onSuccess = (response) => {
        this.isPageReady = true;
        this.actions = response.actions;
        this.cabinets = response.cabinets;
        this.administrators = response.administrators;
        this.employees = response.employees;
        this.suggestedHours = response.suggested_hours;
        this.scheduleDays = response.schedule_days;
        this.workEndsTime = response.work_ends_time;
        this.workStartsTime = response.work_starts_time;
        this.workMode = response.work_mode;
        const { title, body } = response.on_boarding_status;
        this.checkAndUpdate(title, body);
      };
      const onFail = (error) => {
        if (error.title) {
          this.notifyFail(error.title);
        }
      };
      this
        .$workScheduleGateway
        .getByMonth(yearAndMonth)
        .then((result) => { result.fold(onFail, onSuccess); });
    },
    /**
     * @description Обработать печать графика работы ассистента на месяц.
     * @returns {void}
     */
    printAdministratorScheduleByMonth() {
      const onSuccess = (response) => {
        const blob = new Blob([response], { type: 'text/html' });
        const container = this.$refs.printView;
        container.setAttribute('src', URL.createObjectURL(blob));
        container.onload = () => {
          container.contentWindow.print();
          this.isShownPrintDocumentsDialog = false;
        };
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$workScheduleGateway
        .viewWorkScheduleAdministratorForPrint(this.yearAndMonth)
        .then((result) => {
          result.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Обработать печать графика работы ассистента на месяц.
     * @returns {void}
     */
    printAssistantScheduleByMonth() {
      const onSuccess = (response) => {
        const blob = new Blob([response], { type: 'text/html' });
        const container = this.$refs.printView;
        container.setAttribute('src', URL.createObjectURL(blob));
        container.onload = () => {
          container.contentWindow.print();
          this.isShownPrintDocumentsDialog = false;
        };
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$workScheduleGateway
        .viewWorkScheduleAssistantForPrint(this.yearAndMonth)
        .then((result) => {
          result.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Обработать печать графика работы докторов на месяц.
     * @returns {void}
     */
    printDoctorScheduleByMonth() {
      const onSuccess = (response) => {
        const blob = new Blob([response], { type: 'text/html' });
        const container = this.$refs.printView;
        container.setAttribute('src', URL.createObjectURL(blob));
        container.onload = () => {
          container.contentWindow.print();
          this.isShownPrintDocumentsDialog = false;
        };
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$workScheduleGateway
        .viewWorkScheduleDoctorForPrint(this.yearAndMonth)
        .then((result) => {
          result.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Получить список ассистентов.
     * @returns {any[]}
     */
    fetchAssistantList() {
      const onSuccess = (response) => {
        this.assistantsList = response.data;
      };
      const onFail = (error) => {
        if (error.title) {
          this.notifyFail(error.title);
        }
      };
      this
        .$workScheduleGateway
        .viewAssistantList()
        .then((result) => { result.fold(onFail, onSuccess); });
    },
    printPage() {
      this.isShownPrintDocumentsDialog = false;
      this.$nextTick(() => {
        const rect = this.$refs.scheduleTable.getBoundingClientRect();
        const { width } = this.$refs.scheduleTableHeader.getBoundingClientRect();
        const height = Math.max(rect.height, 1000);
        const css = `@media print { @page { size: landscape; size: ${width}px ${height}px; } }`;
        const head = document.head || document.getElementsByTagName('head')[0];
        const style = document.createElement('style');
        head.appendChild(style);
        if (style.styleSheet) {
          style.styleSheet.cssText = css;
        } else {
          style.appendChild(document.createTextNode(css));
        }
        window.print();
        head.removeChild(style);
      });
    },
    isShowEmployeeWorkTime({ actions }) {
      return actions.includes(ACTION_CHANGE_EMPLOYEE_WORK_TIME);
    },
    /**
     * @description Получить идентификатор кабинета рабочего дня сотрудника.
     * @param {number}   employeeId Идентификатор сотрудника.
     * @param {object[]} periods    Рабочие периоды дня.
     * @returns {(number|null)}
     */
    getEmployeeWorkingCabinetId(employeeId, periods) {
      if (periods[0]) {
        return periods[0].cabinets[0].id;
      }
      const workingCabinets = this
        .employees
        .filter(({ id }) => id === employeeId)
        .map(({ cabinet_id: cabinetId }) => cabinetId);
      if (workingCabinets.length) {
        return workingCabinets[0];
      }
      return this.cabinets[0] ? this.cabinets[0].id : null;
    },
    /**
     * Получить инициалы имени и фамилии сотрудника.
     * @param {{name:string}} employee Сотрудник.
     * @returns {string}
     */
    getEmployeeInitials(employee) {
      const parts = employee.name.split(' ').slice(0, 2);
      return parts.reduce((initials, part) => initials + part.charAt(0), '');
    },
    /**
     * @description Получить настройки режима работы.
     * @param {any} workMode Режим работы.
     * @returns {any}
     */
    getEmployeeWorkModeOptions(workMode) {
      const options = Object.values(workMode)[0];
      if (options) {
        return options;
      }
      return [];
    },
    /**
     * @description Получить идентификатор режима работы.
     * @param {any} workMode Режим работы.
     * @returns {string}
     */
    getEmployeeWorkModeId(workMode) {
      const modeId = Object.keys(workMode)[0];
      if (modeId) {
        return modeId;
      }
      return 'by-clinic';
    },
    /**
     * @description Получить время окончания работы дня.
     * @param {(object|null)} workPeriod Данные по рабочему периоду дня.
     * @returns {string}
     */
    getEndsTime(workPeriod) {
      if (workPeriod) {
        return workPeriod.ends_time;
      }
      return this.workEndsTime;
    },
    /**
     * @description Получить время начала работы дня.
     * @param {(object|null)} workPeriod Данные по рабочему периоду дня.
     * @returns {string}
     */
    getStartsTime(workPeriod) {
      if (workPeriod) {
        return workPeriod.starts_time;
      }
      return this.workStartsTime;
    },
    /**
     * @description Является выходным днем по графику клиники.
     * @param {string} isoDate
     * @returns {boolean}
     */
    isClinicDayOff(isoDate) {
      const day = this.scheduleDays.find((candidate) => candidate.date === isoDate);
      if (day) {
        return day.tags.includes('off');
      }
      return false;
    },
    /**
     * @description Обработать закрытие формы редактирования рабочего дня клиники.
     * @returns {void}
     */
    onCancelFormWorkDay() {
      this.selectedClinicDay = null;
    },
    /**
     * @description Обработать изменение значений полей формы.
     * @param {string}  form     Название формы.
     * @param {boolean} isChange Есть изменения.
     * @returns {void}
     */
    onChangeFieldValues(form, isChange) {
      this.needConfirmEmployeeForm = isChange;
    },
    /**
     * @description Обработать клик по рабочему дню клиники.
     * @param {any}        day      День.
     * @param {string}     date     Дата.
     * @param {MouseEvent} event    Событие.
     * @returns {void}
     */
    onClickDay(day, date, event) {
      const isDayOff = day.tags.includes('off');
      if (isDayOff) {
        this.onSubmitWorkDay({
          date,
          from: this.getStartsTime(day.work_period),
          isOff: false,
          to: this.getEndsTime(day.work_period),
        });
      } else {
        this.selectedClinicDay = null;
        this.$nextTick(() => {
          this.selectedClinicDay = {
            dateTitle: day.title,
            datetime: date,
            from: this.getStartsTime(day.work_period),
            to: this.getEndsTime(day.work_period),
            x: event.clientX,
            y: event.clientY,
          };
        });
      }
    },
    /**
     * @description Обработать закрытие формы редактирования рабочего дня сотрудника.
     * @returns {void}
     */
    onCancelFormEmployeeWorkDay() {
      this.selectedEmployeeDay = Maybe.none();
    },
    /**
     * @description Обработать клик по рабочему дню сотрудника.
     * @param {any}        employee    Сотрудник.
     * @param {MouseEvent} event       Событие.
     * @param {any}        scheduleDay День.
     * @returns {void}
     */
    onClickEmployeeDay(employee, event, scheduleDay) {
      if (this.isClinicDayOff(scheduleDay.date)) {
        this.notifyImportanceFail(
          'Этот день в графике клиники установлен как выходной.'
            + ' Сначала измените график работы клиники',
        );
        return;
      }
      const {
        date: datetime,
        periods,
        suggested_hours: suggestedHours,
        title: dateTitle,
      } = scheduleDay;
      const selectDay = (initialPeriods) => {
        const { available_work_cabinet_ids: availableWorkCabinetIds } = employee;
        const { clientX: x, clientY: y } = event;
        this.selectedEmployeeDay = Maybe.some({
          availableWorkCabinetIds,
          dateTitle,
          datetime,
          employeeId: employee.id,
          employeeName: employee.name,
          initialPeriods,
          key: `${employee.id}-${datetime}`,
          suggestedHours,
          x,
          y,
        });
      };
      if (periods.length > 0) {
        const [workPeriod] = periods;
        const initialPeriods = workPeriod.cabinets.map((cabinet) => ({
          initialCabinetId: cabinet.id,
          initialWorkTimes: cabinet.periods.map((period) => ({
            initialFrom: period.starts_time,
            initialTo: period.ends_time,
            initialAssistantId: parseInt(period.assistant_id, 10),
          })),
        }));
        selectDay(initialPeriods);
      } else {
        const cabinetId = this.getEmployeeWorkingCabinetId(employee.id, periods) || null;
        const from = employee.work_starts_time || '00:00';
        const to = employee.work_ends_time || '00:00';
        const initialPeriods = [{
          initialCabinetId: cabinetId,
          initialWorkTimes: [{
            initialFrom: from,
            initialTo: to,
          }],
        }];
        selectDay(initialPeriods);
      }
    },
    /**
     * @description Обработать ответ шлюза.
     * @param {Either} response Ответ.
     * @returns {void}
     */
    onGatewayResponse(response) {
      const onLeft = ({ title, tags }) => {
        if (tags && tags.includes('hight-importance')) {
          this.notifyImportanceFail(title);
        } else {
          this.notifyFail(title);
        }
      };
      const onRight = ({ message }) => {
        this.fetchScheduleByMonth(this.yearAndMonth);
        if (message) {
          this.notifySuccess(message);
          this.onCancelFormEmployeeWorkDay();
          this.onCancelFormWorkDay();
          this.onCancelFormAdministratorWorkDay();
        }
        if (this.closePopover) { this.closePopover(); }
      };
      response.fold(onLeft, onRight);
    },
    /**
     * @description Обработать отправку данных дня сотрудника.
     * @param {string} employeeId Идентификатор сотрудника.
     * @param {any}    day        Данные дня.
     * @returns {void}
     */
    onSubmitEmployeeWorkDay(employeeId, day, closePopover) {
      this.closePopover = closePopover;
      if (day.isOff) {
        this
          .$workScheduleGateway
          .setEmployeeDayOff(employeeId, day.date)
          .then(this.onGatewayResponse);
      } else {
        this
          .$workScheduleGateway
          .setEmployeeWorkDay(
            day.cabinets,
            employeeId,
            day.date,
          )
          .then(this.onGatewayResponse);
      }
    },
    /**
     * @description Обработать редактирование профиля сотрудника.
     * @param {EmployeeEditRequest} request Запрос.
     * @returns {void}
     */
    onSubmitEmployeeEdit(request) {
      this
        .sendEmployeeProfileEditRequest(request)
        .then(() => {
          this.resetEmployeeProfile();
          this.fetchScheduleByMonth(this.yearAndMonth);
        })
        .catch(() => {});
    },
    /**
     * @description Обработать отправку базового времени работы.
     * @param {number} employeeId Идентификатор сотрудника.
     * @param {string} startsTime Время начала.
     * @param {string} endsTime   Время окончания.
     * @returns {void}
     */
    onSubmitEmployeeWorkTime(employeeId, startsTime, endsTime, closePopover) {
      this.closePopover = closePopover;
      this
        .$workScheduleGateway
        .setEmployeeBasicWorkTime(employeeId, endsTime, startsTime)
        .then(this.onGatewayResponse);
    },
    /**
     * @description Обработать отправку данных дня.
     * @param {any} day День.
     * @returns {void}
     */
    async onSubmitWorkDay(day, closePopover) {
      this.closePopover = closePopover;
      if (day.isOff) {
        this
          .$workScheduleGateway
          .setDayOff(day.date)
          .then(this.onGatewayResponse);
      } else {
        this
          .$workScheduleGateway
          .setWorkDay(day.date, day.from, day.to)
          .then(this.onGatewayResponse);
      }
    },
    /**
     * @description Обработать изменение рабочего режима.
     * @param {string}    workMode     Идентификатор рабочего режима.
     * @param {boolean}   onlySelected Только для выбранного месяца.
     * @param {function}  closePopover Закрыть форму.
     * @param {WeekDay[]} weekdays     Дни недели.
     * @returns {void}
     */
    onSubmitWorkMode(workMode, onlySelected, closePopover, weekdays) {
      const onResponse = (result) => {
        result.forEach(() => this.onGatewayResponse(result));
        result.forEachLeft((error) => {
          this.notifyFail(error.title);
        });
      };
      this.closePopover = closePopover;
      this
        .$workScheduleGateway
        .setWorkMode(workMode, onlySelected, this.yearAndMonth, weekdays)
        .then(onResponse);
    },
    /**
     * @description Обработать изменение базового рабочего времени.
     * @param {string} startsTime Время начала.
     * @param {string} endsTime   Время окончания.
     * @returns {void}
     */
    onSubmitWorkTime(startsTime, endsTime, closePopover) {
      this.closePopover = closePopover;
      this
        .$workScheduleGateway
        .setBasicWorkTime(endsTime, startsTime)
        .then(this.onGatewayResponse);
    },
    /**
     * @description Установить рабочий день для сотрудника по клику на выходной.
     * @param {number} cabinetId  Идентификатор рабочего кабинета.
     * @param {string} date       Дата выходного дня.
     * @param {number} employeeId Идентификатор сотрудника.
     * @param {string} workFrom   Начало рабочего времени сотрудника.
     * @param {string} workTo     Окончание рабочего времени сотрудника.
     * @returns {void}
     */
    setEmployeeWorkDayByOffDay(cabinetId, date, employeeId, workFrom, workTo) {
      const [from, to] = this.scheduleDays.flatMap((item) => {
        if (item.date === date) {
          const clinicWorkStartsTime = this.getStartsTime(item.work_period);
          const clinicWorkEndsTime = this.getEndsTime(item.work_period);
          const isAvailableFrom = workFrom >= clinicWorkStartsTime;
          const isAvailableTo = workTo <= clinicWorkEndsTime;
          return [
            isAvailableFrom ? workFrom : clinicWorkStartsTime,
            isAvailableTo ? workTo : clinicWorkEndsTime,
          ];
        }
        return [];
      });
      this.onSubmitEmployeeWorkDay(employeeId, {
        cabinetId,
        date,
        from,
        to,
      });
    },
    /**
     * @description Обработать достижение цели нажатия на кнопку "Как настроить".
     * @returns {void}
     */
    howToSetUpReachGoal() {
      this.$goal(GOAL_CLICK_HOW_TO_SET_UP_BUTTON);
    },
    /**
     * @description Обработать достижение цели переноса рабочего периода Сотрудников на след. месяц.
     * @returns {void}
     */
    copyEmployeeWorkScheduleReachGoal() {
      this.$goal(GOAL_COPY_EMPLOYEES_WORK_SCHEDULE);
    },
  },
  mixins: [
    administratorMixin,
    employeeProfileMixin,
    onBoardingShowMixin,
    onBoardingMixin,
    notifierMixin,
    settingInstructionMixin,
    mobileMixin,
    builtInDialogMixin,
    copyEmployeeWorkScheduleMixin,
  ],
  name: 'ClinicPanelWorkSchedule',
  provide() {
    return {
      maxWorkIntervalsInDay: this.$options.maxWorkIntervalsInDay,
    };
  },
  watch: {
    yearAndMonth(updatedValue) {
      this.fetchScheduleByMonth(updatedValue);
      this.selectedDateToCopy = new Date(updatedValue);
    },
    isShownPrintDocumentsDialog() {
      if (this.isShownPrintDocumentsDialog) {
        const elements = document.getElementsByTagName('input');
        for (let i = 0; i < elements.length; i += 1) {
          if (elements[i].type === 'radio') {
            elements[i].checked = false;
          }
        }
      }
    },
  },
  maxWorkIntervalsInDay: 3,
};
</script>
<style lang="scss">
@use "@/scss/buttons";

.popover-menu-vuetify {
  max-height: 600px;
  background-color: var(--primary-background-color);
  &.menu-visible {
    overflow: visible;
    contain: none;
  }
}

.schedule-copy-dialog {
  overflow-y: hidden;
  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;
    font-weight: bold;
    .v-text-field {
      margin: 0;
      padding: 0;
    }
  }
  &__text {
    font-size: 16px;
    color: rgba(0,0,0,.87);
  }
  & &__note {
    display: block;
    text-align: center;
    column-gap: 5px;
    color: #EF474C;
    &__sign {
      font-size: 18px;
      font-weight: 600;
      vertical-align: bottom;
    }
  }
  &__employee-rows {
    display: flex;
    flex-direction: column;
    row-gap: 15px;
    padding-block: 10px 15px;
    max-height: 320px;
    overflow: auto;
  }
  &__employee-row {
    display: flex;
    align-items: center;
    gap: 10px;
    &__wrapper {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px 5px;
    }
    &__name {
      color: #0489E0;
      font-weight: bold;
    }
    &__speciality {
      color: #707683;
    }
  }
  &__field {
    &__text {
      color: rgba(0,0,0,.87);
    }
  }
}

.v-card__title {
  word-break: break-word;
}

.schedule {
  max-width: calc(100% - 167px);
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  &-header {
    display: grid;
    grid-template-columns: 500px 1fr;
    align-items: center;
    grid-gap: 0;
    padding: 0 30px 30px 23px;
    &__row {
      display: flex;
      flex-direction: row;
      align-items: flex-end;
      justify-content: space-between;
      gap: 20px;
    }
  }
  &__buttons-container {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
  }
  &__button {
    @include buttons.btn-transparent(250px, 48px, 16px);
    max-width: 250px;
    padding: 0;
    margin: 0;
    &-print {
      @include buttons.btn-transparent(250px, 48px, 16px);
      max-width: 250px;
      padding: 0;
      margin: 0;
      &:before {
        content: "";
        background-image: url("@/assets/img/print.svg");
        background-repeat: no-repeat;
        background-size: contain;
        width: 24px;
        height: 24px;
        margin: 0 8px 0 0;
      }
    }
    &-copy {
      @include buttons.btn-transparent(100%, 48px, 16px);
      max-width: 250px;
      padding: 0;
      margin: 0;
      &:before {
        content: "";
        background-image: url("@/assets/img/copy-blue.svg");
        background-repeat: no-repeat;
        background-size: contain;
        width: 24px;
        height: 24px;
        margin: 0 8px 0 0;
      }
    }
    &-submit {
      @include buttons.btn-blue(250px, 48px, 16px);
      max-width: 250px;
      padding: 0;
      margin: 0;
    }
  }
  &-wrap {
    position: relative;
    height: 100%;
    width: 100%;
    z-index: 0;
    padding: 0 0 150px 0;
  }
  &-table {
    overflow: auto;
    max-height: calc(100vh - 410px);
    &__header {
      width: fit-content;
      display: flex;
      flex-direction: row;
      align-items: flex-end;
      box-shadow: var(--shadow-layer-bottom);
      margin: 0 0 1px 0;
      padding: 0 0 1px 0;
      position: sticky;
      z-index: 3;
      top: -1px;
      background-color: white;
    }
  }
  &__unit {
    display: grid;
    grid-template-columns: 1fr 140px;
    grid-gap: 5px;
    align-items: flex-end;
    padding: 0 10px 15px 35px;
    width: 405px;
    height: 105px;
    background-color: white;
    z-index: 1;
    position: sticky;
    left: 0;
    &.employee {
      padding: 0 0 0 25px;
      height: 56px;
    }
    &__card {
      border: 1px solid rgba(0, 0, 0, 0.12);
      padding: 10px;
      height: 56px;
      display: grid;
      grid-template-columns: 1fr 140px;
      grid-gap: 5px;
      align-items: center;
      width: 381px;
      &:hover {
        border: 1px solid #C2CFE0;
      }
    }
    &__caption {
      color: #192A3E;
      font-weight: 400;
      font-size: 16px;
      line-height: 17px;
    }
    &__row {
      display: flex;
      flex-direction: row;
    }
  }
  &__actions {
    display: flex;
    align-items: baseline;
    &__text {
      font-weight: 400;
      font-size: 12px;
      line-height: 14px;
      color: #707683;
    }
    &__button {
      &-select {
        background-image: url("@/assets/img/checked/checked-grey.svg");
        background-size: contain;
        background-repeat: no-repeat;
        background-position: bottom;
        width: 14px;
        height: 10px;
        margin: 0 30px 0 0;
        &:hover {
          background-image: url("@/assets/img/checked/checked-blue.svg");
        }
        &-show {
          background-image: url("@/assets/img/checked/checked-grey.svg");
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
          background-color: white;
          width: 25px;
          height: 25px;
          border-radius: 25%;
        }
      }
      &-edit {
        background-image: url("@/assets/img/pen-dark.svg");
        background-size: contain;
        background-repeat: no-repeat;
        background-position: bottom;
        transform: translateY(1px);
        width: 12px;
        height: 14px;
        margin: 0 0 0 10px;
        &:hover {
          background-image: url("@/assets/img/pen-blue.svg");
        }
        &-show {
          margin: 0 30px 0 0;
          background-image: url("@/assets/img/pen-dark.svg");
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
          background-color: white;
          width: 25px;
          height: 25px;
          border-radius: 25%;
        }
      }
    }
  }
  &__employee {
    &__avatar, &__initials {
      object-fit: cover;
      width: 35px;
      height: 35px;
      border-radius: 50%;
      margin: 0 10px 0 0;
    }
    &__initials {
      text-align: center;
      background-color: var(--action-color);
      color: white;
      font-weight: bold;
      line-height: 35px;
    }
    &__column {
      display: flex;
      flex-direction: column;
    }
    &__name {
      text-align: left;
      text-decoration: underline;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 160px;
      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
      color: #0489E0;
      margin: 0 0 2px 0;
      cursor:pointer;
      &:disabled {
        text-decoration: none;
      }
    }
    &__speciality {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 160px;
      color: #707683;
      font-weight: 400;
      font-size: 12px;
      line-height: 14px;
    }
  }
  &__days {
    &__body {
      user-select: none;
    }
    &__row {
      display: flex;
      flex-direction: row;
      width: fit-content;
      cursor: pointer;
      &:hover {
        .schedule__unit__card,
        .schedule__days__body.schedule__days__row .schedule__days__cell {
          border-top: 1px solid #0489E0;
          border-bottom: 1px solid #0489E0;
        }
      }
      .schedule__days__cell.weekend {
        border-left: 2px solid rgba(0, 0, 0, 0.35);
        &+.schedule__days__cell.weekend {
          border-right: 3px solid rgba(0, 0, 0, .35);
          border-left: 1px solid rgba(0, 0, 0, .35);
        }
      }
      &.dates {
        z-index: 0;
        position: sticky;
        top: 0;
        background-color: white;
      }
    }
    &__cell {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-direction: column;
      font-weight: 500;
      font-size: 9px;
      line-height: 16px;
      color: var(--reading-color);
      border: 1px solid rgba(0, 0, 0, .12);
      width: 45px;
      height: 56px;
      text-align: center;
      padding: 9px 1px;
      margin: 0 -.5px;
      &.data {
        height: 48px;
        font-size: 12px;
        line-height: 14px;
        padding: 5px 1px;
        color: #707683;
        border: 1px solid var(--primary-background-color);
        margin: 0 -.5px 2px -.5px;
        &.weekend {
          color: #EF474C;
        }
      }
      &.today {
        background: #DAF0FF;
        border-radius: 2px;
        color: var(--reading-color);
      }
      &.clinic {
        background: #0489E0;
        border: 1px solid #F2F6F8;
        color: var(--primary-background-color);
      }
      &.off {
        &:not(.uneditable) {
          background-color: #f7f7f7;
        }
      }
      &:disabled {
        cursor: default;
        filter: saturate(.3);
        pointer-events: none;
        &.off:is(.weekend, .uneditable) {
          background-color: #cfd2d5;
        }
      }
    }
  }
  &__title {
    display: flex;
    flex-flow: row wrap;
    align-items: baseline;
    gap: 10px 30px;
    button {
      font-size: 18px;
      font-weight: 500;
      color: #90a0b7;
      &:hover {
        color: #0489E0;
      }
    }
  }
  &__modal {
    &__img {
      max-width: 100%;
      height: auto;
      object-fit: contain;
      margin: 15px 0;
    }
  }
  &__info {
    padding: 0 30px 0 33px;
    font-size: 18px;
    margin: 40px 0;
    a {
      text-decoration: underline;
    }
    a:hover {
      text-decoration: none;
    }
  }
  &__setting-instruction {
    padding: 0 30px 0 33px;
  }
  &__employee-form {
    &__buttons {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      max-width: 438px;
      width: 100%;
      margin: 10px 0 0 auto;
    }
    &__button {
      &-cancel {
        max-width: 219px;
        @include buttons.btn-transparent(100%, 56px, 20px);
      }
      &-save {
        max-width: 219px;
        @include buttons.btn-blue(100%, 56px, 20px);
        &:disabled {
          background-color: var(--unfocus-color);
          border-color: var(--unfocus-color);
        }
      }
    }
  }
  &__button-back {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 38px 0 0 23px;
  }
  &__popup {
    &__container {
      padding: 20px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    &__radio > input {
      position: absolute;
      z-index: -1;
      opacity: 0;
    }
    &__radio > span {
      display: inline-flex;
      align-items: center;
      user-select: none;
      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
      cursor: pointer;
    }
    &__radio > span::before {
      content: "";
      display: inline-block;
      width: 24px;
      height: 24px;
      flex-shrink: 0;
      flex-grow: 0;
      border: 1px solid #90a0b7;
      border-radius: 50%;
      margin-right: 10px;
      background-repeat: no-repeat;
      background-position: center center;
      background-size: 50% 50%;
    }
    &__radio > input:not(:disabled):not(:checked) + span:hover::before {
      border: 1px solid #0489e0;
    }
    &__radio > input:checked + span::before {
      border: 1px solid #0489e0;
      background-color: #0489e0;
      box-shadow: inset 0 0 0 6px #fff;
    }
  }
  &__subtitle {
    padding: 25px;
    width: 100%;
    position: sticky;
    left: 0;
  }
}
@media print {
  .clinic-panel {
    width: fit-content;
    height: fit-content;
    min-height: fit-content;
  }
  .schedule__actions__button-edit,
  .schedule__actions__button-select,
  .schedule__info,
  .switcher__next,
  .switcher__prev,
  .schedule__button-print {
    display: none;
  }
  .schedule {
    width: fit-content;
    height: fit-content;
    &__buttons {
      display: none;
    }
    &-table {
      padding: 0;
      overflow: hidden;
      &__header {
        box-shadow: none;
      }
    }
    &-header {
      &__row {
        .switcher__prev,
        .switcher__next {
          display: none;
        }
      }
    }
    &-wrap, &-table {
      overflow: hidden;
    }
    &__unit {
      width: 345px;
      &__card {
        width: 320px;
        grid-template-columns: 1fr 1fr;
      }
    }
  }
  .head-panel {
    display: none;
  }
}
@media screen and (orientation: landscape) and (min-width:480px) and (max-width: 950px) {
  .schedule-header {
    padding-bottom: 10px;
  }
  .schedule-wrap {
    padding-bottom: 280px;
  }
}
@media screen and (max-width: 1260px) {
  .schedule {
    &-header {
      grid-template-columns: 1fr;
      grid-gap: 20px;
    }
  }
}
@media screen and (max-width: 960px) {
  .schedule-copy-dialog {
    &__header {
      column-gap: 16px;
    }
  }
  .schedule {
    max-width: 100%;
  }
}
@media screen and (max-width: 700px) {
  .schedule {
    padding: 0 20px;
    max-width: 100%;
    &__button-back {
      padding-left: 0;
    }
    &__title {
      text-align: center;
      max-height: 30vh;
    }
    &-header {
      padding: 20px 0 30px;
      &__row {
        flex-direction: column;
      }
    }
    &-table {
      &__header {
        box-shadow: none;
        position: sticky;
      }
    }
    &__unit {
      min-width: 381px;
      padding: 0 10px 15px 0;
      position: relative;
      &.employee {
        min-width: 381px;
        padding: 0;
      }
      &__card {
        min-width: 381px;
      }
    }
    &__info {
      padding: 0;
    }
    &__setting-instruction {
      padding: 0;
    }
  }
}
@media screen and (max-width: 620px) {
  .schedule {
    padding: 38px 10px 140px;
    &__button-back {
      padding-top: 0;
    }
  }
}
@media screen and (max-width: 480px) {
  .schedule {
    &__buttons {
      grid-template-columns: 1fr 1fr;
      width: 100%;
    }
    &-wrap {
      overflow: visible;
      height: fit-content;
    }
    &__days {
      &__row {
        width: auto;
      }
    }
    &__unit {
      flex: 1;
      grid-template-columns: 1fr;
      padding: 0 0 15px;
      min-width: 100% !important;
      height: auto;
      &__card {
        width: 100%;
        min-width: 100%;
      }
      &__caption {
        width: fit-content;
        font-weight: bold;
      }
    }
    &__employee {
      &__avatar,
      &__initials{
        display: none;
      }
      &__name {
        width: 120px;
      }
      &__speciality {
        width: 120px;
      }
    }
    &-table {
      max-height: calc(100vh - 450px);
    }
    &-table__header {
      width: 100%;
      & > .schedule__unit {
        grid-template-columns: 1fr 140px;
      }
    }
  }
}
@media screen and (max-height: 760px) {
  .schedule {
    &-table {
      max-height: 40vh;
    }
  }
}

</style>
